import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectorRef,
  inject,
  DestroyRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalValuesService } from '../../../../core/services/global-values.service';
import { getSafeImageUrl } from '@app/core';

@Component({
  selector: 'app-order-details-content',
  standalone: false,
  templateUrl: './order-details-content.component.html',
  styleUrl: './order-details-content.component.less',
})
export class OrderDetailsContentComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @Input() serviceOrder: any = null;
  @Input() editPermissions: boolean = false;
  @Input() budgets: any[] = [];
  @Input() documents: any[] = [];
  @Input() activeTabIndex: number = 0;
  @Input() isEditModeEnabled: boolean = false;

  @Output() fieldUpdate = new EventEmitter<{
    path: string;
    value: any;
    previousValue?: any;
  }>();
  @Output() budgetClick = new EventEmitter<any>();

  // Estado de edición
  currentEditingField: string | null = null;
  backupFieldValues: { [key: string]: any } = {};
  tempFieldValues: { [key: string]: any } = {};
  afterViewInit: boolean = false;

  // Datos de referencia
  users: any[] = [];
  costCenters: any[] = [];

  protected readonly globalValuesService = inject(GlobalValuesService);
  protected readonly getSafeImageUrl = getSafeImageUrl;
  private destroyRef = inject(DestroyRef);

  // Propiedades computadas para Service Orders
  get serviceOrderId(): number {
    return this.serviceOrder?.id || 0;
  }

  get serviceOrderName(): string {
    return this.serviceOrder?.subtaskName || '-';
  }

  get serviceOrderNameForInput(): string {
    return (
      this.serviceOrder?.subtaskName ||
      this.serviceOrder?.reference ||
      this.serviceOrder?.name ||
      ''
    );
  }

  get serviceOrderState(): string {
    return this.serviceOrder?.state || '-';
  }

  get assignedUser(): string {
    // Primero intentar usar assignedTo que viene del backend, PERO verificar consistencia
    const assignedTo = this.serviceOrder?.assignedTo;

    // Buscar el ID del usuario asignado (para verificar consistencia)
    const assignedUserId =
      this.serviceOrder?.assignedUserId || this.serviceOrder?.assignedToId;

    if (assignedTo?.partner) {
      // 🔥 VERIFICAR SI assignedTo.partner.id COINCIDE CON assignedUserId
      const partnerMatches =
        assignedUserId && assignedTo.partner.id === assignedUserId;

      if (partnerMatches) {
        const result = this.getUserDisplayName(assignedTo.partner);
        return result || '-';
      }
    }

    // Ya tenemos assignedUserId de arriba
    if (assignedUserId && this.users && this.users.length > 0) {
      const user = this.users.find((u) => u.id === assignedUserId);
      if (user) {
        const result = this.getUserDisplayName(user);
        return result || '-';
      }
    }

    return assignedUserId ? `ID: ${assignedUserId}` : '-';
  }

  private getUserNameById(userId: number | null): string {
    if (!userId) return '-';
    // Buscar en la estructura anidada: serviceOrder.service.project.partner
    const partners = this.serviceOrder?.service?.project?.partner;
    if (partners && Array.isArray(partners)) {
      const partner = partners.find((p: any) => p.id === userId);
      return partner ? partner.name || '-' : '-';
    }
    return '-';
  }

  get startDate(): string {
    if (!this.serviceOrder?.startDate) return '-';
    try {
      // Si viene en formato dd/mm/yy, convertirlo primero
      let date: Date;
      if (
        typeof this.serviceOrder.startDate === 'string' &&
        this.serviceOrder.startDate.includes('/')
      ) {
        const [day, month, year] = this.serviceOrder.startDate.split('/');
        // Asumir que yy es 20yy
        const fullYear = year.length === 2 ? `20${year}` : year;
        date = new Date(`${fullYear}-${month}-${day}`);
      } else {
        date = new Date(this.serviceOrder.startDate);
      }
      return date.toLocaleDateString('es-ES');
    } catch {
      return '-';
    }
  }

  get startDateForPicker(): Date | null {
    if (!this.serviceOrder?.startDate) return null;
    try {
      // Si viene en formato dd/mm/yy, convertirlo primero
      if (
        typeof this.serviceOrder.startDate === 'string' &&
        this.serviceOrder.startDate.includes('/')
      ) {
        const [day, month, year] = this.serviceOrder.startDate.split('/');
        // Asumir que yy es 20yy
        const fullYear = year.length === 2 ? `20${year}` : year;
        return new Date(`${fullYear}-${month}-${day}`);
      } else {
        return new Date(this.serviceOrder.startDate);
      }
    } catch {
      return null;
    }
  }

  get endDate(): string {
    if (!this.serviceOrder?.endDate) return '-';
    try {
      // Si viene en formato dd/mm/yy, convertirlo primero
      let date: Date;
      if (
        typeof this.serviceOrder.endDate === 'string' &&
        this.serviceOrder.endDate.includes('/')
      ) {
        const [day, month, year] = this.serviceOrder.endDate.split('/');
        // Asumir que yy es 20yy
        const fullYear = year.length === 2 ? `20${year}` : year;
        date = new Date(`${fullYear}-${month}-${day}`);
      } else {
        date = new Date(this.serviceOrder.endDate);
      }
      return date.toLocaleDateString('es-ES');
    } catch {
      return '-';
    }
  }

  get endDateForPicker(): Date | null {
    if (!this.serviceOrder?.endDate) return null;
    try {
      // Si viene en formato dd/mm/yy, convertirlo primero
      if (
        typeof this.serviceOrder.endDate === 'string' &&
        this.serviceOrder.endDate.includes('/')
      ) {
        const [day, month, year] = this.serviceOrder.endDate.split('/');
        // Asumir que yy es 20yy
        const fullYear = year.length === 2 ? `20${year}` : year;
        return new Date(`${fullYear}-${month}-${day}`);
      } else {
        return new Date(this.serviceOrder.endDate);
      }
    } catch {
      return null;
    }
  }

  get costCenterName(): string {
    const name = this.serviceOrder?.costCenter?.name || '-';
    return name;
  }

  // Propiedad computada para forzar actualización del display
  get costCenterDisplay(): string {
    return this.costCenterName;
  }

  get priorityName(): string {
    const priority = this.serviceOrder?.priority;
    if (priority === null || priority === undefined) return '-';

    if (typeof priority === 'string') {
      const priorityMap: { [key: string]: string } = {
        Low: 'Baja',
        Medium: 'Media',
        High: 'Alta',
        VeryHigh: 'Muy Alta',
      };
      return priorityMap[priority] || priority;
    }
    return '-';
  }

  get macroTask(): string {
    return this.serviceOrder?.service?.name || '-';
  }

  get expectedClosingDate(): string {
    return this.serviceOrder?.expectedClosure
      ? new Date(this.serviceOrder.expectedClosure).toLocaleDateString('es-ES')
      : '-';
  }

  get expectedClosingDateForPicker(): Date | null {
    return this.serviceOrder?.expectedClosure
      ? new Date(this.serviceOrder.expectedClosure)
      : null;
  }

  get availablePartners(): any[] {
    return this.users || [];
  }

  // Propiedad para forzar la actualización del select
  get partnersSelectId(): string {
    return `partners-${this.users?.length || 0}-${Date.now()}`;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Cargar datos de referencia
    this.globalValuesService.costCenters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((costCenters: any[]) => {
        this.costCenters = costCenters || [];
        this.cdr.detectChanges();
      });

    this.globalValuesService.users$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users: any[]) => {
        this.users = users || [];
        this.cdr.detectChanges();
        // Delay adicional para asegurar que el select se actualice
        setTimeout(() => this.cdr.detectChanges(), 100);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editPermissions']) {
      if (!this.editPermissions && this.currentEditingField) {
        this.cancelFieldEdit(this.currentEditingField);
      }
    }
    if (changes['serviceOrder']) {
      // Si viene assignedToId del backend, copiarlo a assignedUserId para el componente
      if (
        this.serviceOrder?.assignedToId &&
        !this.serviceOrder.assignedUserId
      ) {
        this.serviceOrder.assignedUserId = this.serviceOrder.assignedToId;
      }

      // Si viene costCenterId del backend, buscar el objeto costCenter correspondiente
      if (
        this.serviceOrder?.costCenterId &&
        !this.serviceOrder.costCenter &&
        this.costCenters.length > 0
      ) {
        const costCenter = this.costCenters.find(
          (cc) => cc.id === this.serviceOrder.costCenterId
        );
        if (costCenter) {
          this.serviceOrder.costCenter = costCenter;
        }
      }

      // Forzar detección de cambios cuando cambia el serviceOrder
      this.cdr.detectChanges();
      // Delay adicional para asegurar que el select se actualice
      setTimeout(() => this.cdr.detectChanges(), 100);
    }
  }

  ngAfterViewInit(): void {
    this.afterViewInit = true;
  }

  // Funciones de edición
  isFieldEditing(fieldName: string): boolean {
    return this.isEditModeEnabled && this.currentEditingField === fieldName;
  }

  onFieldClick(fieldName: string, event: Event): void {
    if (!this.isEditModeEnabled) return;
    event.stopPropagation();

    // Si ya estamos editando otro campo, cancelarlo primero
    if (this.currentEditingField && this.currentEditingField !== fieldName) {
      this.cancelFieldEdit(this.currentEditingField);
    }

    this.startFieldEdit(fieldName);
  }

  startFieldEdit(fieldName: string): void {
    if (!this.serviceOrder) return;

    this.currentEditingField = fieldName;

    // Hacer backup del valor actual
    if (fieldName === 'name') {
      this.backupFieldValues[fieldName] =
        this.serviceOrder.name || this.serviceOrder.reference;
      this.tempFieldValues[fieldName] =
        this.serviceOrder.name || this.serviceOrder.reference || '';
    } else if (fieldName === 'state') {
      this.backupFieldValues[fieldName] = this.serviceOrder[fieldName];
      this.tempFieldValues[fieldName] = this.serviceOrder[fieldName] || '';
    } else if (fieldName === 'assignedUserId') {
      this.backupFieldValues[fieldName] = this.serviceOrder[fieldName];
      this.tempFieldValues[fieldName] = this.serviceOrder[fieldName] || null;
    } else if (
      fieldName === 'startDate' ||
      fieldName === 'endDate' ||
      fieldName === 'expectedClosure'
    ) {
      this.backupFieldValues[fieldName] = this.serviceOrder[fieldName];
      this.tempFieldValues[fieldName] = this.serviceOrder[fieldName]
        ? new Date(this.serviceOrder[fieldName])
        : null;
    } else if (
      fieldName === 'costCenterId' ||
      fieldName === 'priority' ||
      fieldName === 'macroTask' ||
      fieldName === 'subtaskName'
    ) {
      this.backupFieldValues[fieldName] = this.serviceOrder[fieldName];
      this.tempFieldValues[fieldName] = this.serviceOrder[fieldName] || '';
    }

    this.cdr.detectChanges();
  }

  onFieldBlur(fieldName: string, event: any): void {
    const newValue = event.target?.value || this.tempFieldValues[fieldName];

    if (fieldName === 'name') {
      // Para el nombre, usar reference como campo principal
      if (this.serviceOrder.reference !== newValue) {
        this.fieldUpdate.emit({
          path: 'reference',
          value: newValue,
          previousValue: this.backupFieldValues[fieldName],
        });
      }
    } else if (
      fieldName === 'state' ||
      fieldName === 'assignedUserId' ||
      fieldName === 'costCenterId' ||
      fieldName === 'priority' ||
      fieldName === 'subtaskName'
    ) {
      if (newValue !== this.backupFieldValues[fieldName]) {
        // Actualizar el serviceOrder con el nuevo valor
        if (this.serviceOrder) {
          this.serviceOrder[fieldName] = newValue;
        }
        this.fieldUpdate.emit({
          path: fieldName,
          value: newValue,
          previousValue: this.backupFieldValues[fieldName],
        });
      }
    } else if (
      fieldName === 'startDate' ||
      fieldName === 'endDate' ||
      fieldName === 'expectedClosure'
    ) {
      if (newValue !== this.backupFieldValues[fieldName]) {
        // Actualizar el serviceOrder con el nuevo valor
        if (this.serviceOrder) {
          this.serviceOrder[fieldName] = newValue
            ? newValue.toISOString()
            : null;
        }
        this.fieldUpdate.emit({
          path: fieldName,
          value: newValue ? newValue.toISOString() : null,
          previousValue: this.backupFieldValues[fieldName],
        });
      }
    } else if (fieldName === 'macroTask') {
      if (newValue !== this.backupFieldValues[fieldName]) {
        // Actualizar el serviceOrder con el nuevo valor
        if (this.serviceOrder) {
          this.serviceOrder[fieldName] = newValue;
        }
        this.fieldUpdate.emit({
          path: fieldName,
          value: newValue,
          previousValue: this.backupFieldValues[fieldName],
        });
      }
    }

    this.cancelFieldEdit(fieldName);
    this.cdr.detectChanges();
  }

  onDateChange(fieldName: string, date: Date): void {
    this.tempFieldValues[fieldName] = date;
    // Para las fechas, guardar automáticamente al cambiar
    if (date !== this.backupFieldValues[fieldName]) {
      // Actualizar el serviceOrder con el nuevo valor
      if (this.serviceOrder) {
        this.serviceOrder[fieldName] = date ? date.toISOString() : null;
      }
      this.fieldUpdate.emit({
        path: fieldName,
        value: date ? date.toISOString() : null,
        previousValue: this.backupFieldValues[fieldName],
      });
    }
    this.cancelFieldEdit(fieldName);
    this.cdr.detectChanges();
  }

  onSelectChange(fieldName: string, value: any): void {
    this.tempFieldValues[fieldName] = value;

    if (value === this.backupFieldValues[fieldName]) {
      this.cancelFieldEdit(fieldName);
      return;
    }

    if (this.serviceOrder) {
      let updatedServiceOrder = {
        ...this.serviceOrder,
        [fieldName]: value,
      };

      // Para costCenterId, también actualizar el objeto costCenter completo
      if (fieldName === 'costCenterId') {
        const selectedCostCenter = this.costCenters.find(
          (cc) => cc.id === value
        );
        updatedServiceOrder = {
          ...updatedServiceOrder,
          costCenter: selectedCostCenter || null,
        };
      }

      // 🔥 MUY IMPORTANTE - Mantener assignedTo si no se está cambiando
      if (fieldName !== 'assignedToId') {
        updatedServiceOrder.assignedTo = this.serviceOrder.assignedTo;
      } else {
        updatedServiceOrder.assignedTo = null; // Solo resetear si se está cambiando assignedToId
      }

      this.serviceOrder = updatedServiceOrder;
    }

    this.fieldUpdate.emit({
      path: fieldName,
      value,
      previousValue: this.backupFieldValues[fieldName],
    });

    // Forzar actualización del componente antes de salir del modo edición
    this.cdr.detectChanges();

    setTimeout(() => {
      this.cancelFieldEdit(fieldName);
      this.cdr.detectChanges();
      // Delay adicional para asegurar que las propiedades computadas se actualicen
      setTimeout(() => this.cdr.detectChanges(), 50);
    }, 0);
  }

  cancelFieldEdit(fieldName: string): void {
    this.currentEditingField = null;
    this.backupFieldValues[fieldName] = null;
    this.tempFieldValues[fieldName] = null;
    this.cdr.detectChanges();
  }

  getUserDisplayName(partner: any): string {
    if (!partner) return '';

    // Si partner tiene name directamente (como assignedTo.partner)
    if (partner.name) {
      const name = partner.name;
      const surname = partner.surName || '';
      const fullName = `${name} ${surname}`.trim();

      if (fullName) return fullName;
    }

    // Si partner tiene partner.name anidado (como en la lista de usuarios)
    if (partner.partner?.name) {
      const name = partner.partner.name;
      const surname = partner.partner.surName || '';
      const fullName = `${name} ${surname}`.trim();

      if (fullName) return fullName;
    }

    // Fallback al email si existe
    if (partner.email) return partner.email;

    // Último fallback
    return partner.id?.toString() || 'Usuario sin nombre';
  }

  trackByPartner(index: number, partner: any): any {
    return partner?.id || index;
  }

  getDisplayValue(value: any, defaultValue: string): string {
    return value || defaultValue;
  }

  onBudgetClick(budget: any, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.budgetClick.emit(budget);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return '-';
    }
  }

  getPartnerName(partnerId: number | null): string {
    if (!partnerId) return '-';
    const partners = this.globalValuesService.partnerlist || [];
    const partner = partners.find((p: any) => p.id === partnerId);
    if (partner) {
      return `${partner.name || ''} ${partner.surName || ''}`.trim() || '-';
    }
    return '-';
  }

  getPartnerAvatar(partnerId: number | null): string | null {
    if (!partnerId) return null;
    const partners = this.globalValuesService.partnerlist || [];
    const partner = partners.find((p: any) => p.id === partnerId);
    return partner?.avatar || null;
  }

  getStarsArray(priority: string): number[] {
    return [1, 2, 3, 4]; // Siempre mostrar 4 estrellas
  }

  getFilledStars(priority: string): number {
    switch (priority) {
      case 'Low':
        return 1;
      case 'Medium':
        return 2;
      case 'High':
        return 3;
      case 'VeryHigh':
        return 4;
      default:
        return 0;
    }
  }

  formatDateWithIcon(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      // Si viene en formato dd/mm/yy, convertirlo primero
      let date: Date;
      if (typeof dateString === 'string' && dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        // Asumir que yy es 20yy
        const fullYear = year.length === 2 ? `20${year}` : year;
        date = new Date(`${fullYear}-${month}-${day}`);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '-';

      const day = date.getDate();
      const monthNames = [
        'ENE',
        'FEB',
        'MAR',
        'ABR',
        'MAY',
        'JUN',
        'JUL',
        'AGO',
        'SEP',
        'OCT',
        'NOV',
        'DIC',
      ];
      const month = monthNames[date.getMonth()];

      return `${day} ${month}`;
    } catch {
      return '-';
    }
  }

  getProjectName(projectId: number): string {
    // For service orders, we might not have projects directly associated
    // This would need to be implemented based on how projects are linked to service orders
    return `Proyecto ${projectId}`;
  }
}
